// Connexion et déconnexion : la même scène, à l'endroit et à l'envers.
//
// On entre dans la plateforme, on en sort — et dans les deux cas l'écran ne bougeait pas
// entre le clic et l'arrivée de l'autre page. Les deux formulaires sont en
// `data: { turbo: false }`, donc pas de barre de progression Turbo : le navigateur garde
// l'ancien document peint jusqu'au premier paint du suivant, et l'app (ou le formulaire de
// connexion) reste là, intacte et muette, pendant toute l'attente.
//
// Et l'attente est longue côté connexion : le POST, puis TROIS redirections (sign_in → root
// → signed_in_root → tableau de bord rails_admin), puis le rendu complet de la page
// d'arrivée.
//
// C'est le vortex de la bascule d'espace qui joue la scène (cf. masquerade_vortex.ts), avec
// deux directions de plus. Ce choix plutôt qu'une seconde mécanique :
//
//   • franchir la porte de la plateforme, c'est le même geste que passer d'un espace à
//     l'autre — la page se décompose, passe par le vortex, et la suivante se rassemble de
//     l'autre côté. Ce qui est au centre, c'est la marque de l'espace, pas un visage ;
//   • tout y est déjà, et rien de tout ça n'est anodin à réécrire : la décomposition en
//     morceaux de squelette, l'implosion en spirale, la coquille rendue par le serveur pour
//     l'arrivée, le réassemblage, le respect de `prefers-reduced-motion`, les filets de
//     sécurité et le verrou anti-rejeu.
//
// Ce fichier ne fait donc qu'une chose : brancher des DÉPARTS DE FORMULAIRE sur
// `runVortexTransition`, qui était jusqu'ici réservé au mode arcade — le seul autre départ
// de l'app qui ne soit pas un lien. L'arrivée, elle, n'a rien de spécifique : le serveur
// pose la charge utile sur <body> comme pour n'importe quel vortex (cf. layouts/_body.html.erb).
//
// Les deux départs ne partent pas au même moment :
//
//   • la déconnexion n'a rien à valider : l'implosion part au clic ;
//   • la connexion, si. Partie au clic, l'implosion se jouait aussi sur un mot de passe faux
//     — la page s'aspirait dans le vortex, puis revenait d'un bloc avec son message d'erreur
//     —, et sur la demande de code à usage unique, qui ne mène pas au tableau de bord non
//     plus. Le formulaire part donc en fetch, et c'est le spinner du bouton
//     (form_loading_controller.js) qui répond au clic pendant toute la validation :
//     identifiants, extranet VEOS, code à usage unique. Le vortex n'est lancé qu'au signal
//     du serveur — la session est ouverte, la suite est le tableau de bord (cf.
//     Users::SessionsController#redirect_to).
//
// Hotwire Native n'est pas concerné : l'app y a ses propres transitions d'écran, et le
// serveur n'y rend ni la charge utile ni la coquille d'arrivée. La déconnexion y garde son
// rideau (cf. sign_out_transition.ts), qui reste branché sur un attribut distinct.

import { runVortexTransition } from './masquerade_vortex';

// Posé par le serveur sur le <form> — celui de la connexion, celui du code à usage unique,
// et le <form> que `button_to` rend pour la déconnexion. Jamais sur le bouton : c'est le
// formulaire qui émet `submit`.
const FORM_SELECTOR = 'form[data-auth-vortex]';

// Le protocole partagé avec Users::SessionsController. L'en-tête de requête annonce un
// client qui sait jouer le départ ; celui de réponse accompagne le 204 du signal et donne
// l'adresse où aller.
const REQUEST_HEADER = 'X-Auth-Vortex';
const LOCATION_HEADER = 'X-Auth-Vortex-Location';

// Ce qui change d'une page Devise à l'autre sous la même mise en page : le contenu (le
// formulaire, l'étape du code à usage unique) et les messages.
const DEVISE_PAGE_PARTS = ['.main-content-column', '#flashes-container'];

// Un formulaire parti ne repart pas : double clic, Entrée pendant l'attente. Vidé au retour
// depuis le bfcache, qui rend la page telle qu'on l'a quittée — formulaire compris, alors
// qu'il doit pouvoir repartir (cf. `restore` dans form_loading_controller.js).
let departed = new WeakSet<HTMLFormElement>();

const directionOf = (form: HTMLFormElement): string | null => {
  try {
    return JSON.parse(form.dataset.authVortex ?? '')?.direction ?? null;
  } catch {
    return null;
  }
};

// L'adresse vient d'un en-tête de réponse : on ne navigue que sur notre propre origine.
const sameOriginUrl = (raw: string | null): string | null => {
  if (!raw) return null;

  try {
    const url = new URL(raw, window.location.origin);
    return url.origin === window.location.origin ? url.toString() : null;
  } catch {
    return null;
  }
};

const isHtml = (response: Response) =>
  /^(text\/html|application\/xhtml\+xml)\b/.test(response.headers.get('Content-Type') ?? '');

// Un champ inséré dans le DOM n'honore pas `autofocus` : seul le chargement d'un document le
// fait. Le premier visible — la page de connexion rend un onglet par extranet, masqués sauf
// un.
const focusAutofocus = (root: ParentNode) => {
  const field = Array.from(root.querySelectorAll<HTMLElement>('[autofocus]')).find(
    (element) => element.getClientRects().length > 0
  );
  field?.focus();
};

// Identifiants refusés, code à usage unique demandé ou refusé, jeton CSRF périmé : le serveur
// a répondu par une page Devise, sous la mise en page qu'on a déjà sous les yeux. On en greffe
// le contenu et les messages, comme l'aurait affiché le document neuf d'une soumission
// classique — champs, message d'erreur, autofocus —, sans en payer le rechargement.
//
// Une réponse qui n'est pas une page Devise (5xx, 429 en texte brut de Rack::Attack) n'a rien
// à greffer : `false`, et c'est le navigateur qui s'en charge.
async function adoptDevisePage(response: Response): Promise<boolean> {
  if (!isHtml(response)) return false;

  let html: string;
  try {
    html = await response.text();
  } catch {
    return false;
  }

  const incoming = new DOMParser().parseFromString(html, 'text/html');
  if (incoming.body?.dataset.devisePage !== 'true') return false;

  const swaps = DEVISE_PAGE_PARTS.map((selector) => ({
    current: document.querySelector(selector),
    next: incoming.querySelector(selector),
  }));
  if (swaps.some(({ current, next }) => !current || !next)) return false;

  swaps.forEach(({ current, next }) => current!.replaceWith(document.adoptNode(next!)));
  if (incoming.title) document.title = incoming.title;
  focusAutofocus(document);
  return true;
}

async function signIn(form: HTMLFormElement): Promise<void> {
  let response: Response;
  try {
    response = await fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: {
        // Une page, comme pour une soumission classique : c'est elle qu'on greffe si la
        // connexion n'aboutit pas. Surtout pas de flux Turbo en tête de liste — Devise ne
        // rend sa page d'erreur qu'aux formats « navigationnels » (cf.
        // `config.navigational_formats`), et répondrait sinon un 401 en texte brut.
        Accept: 'text/html, application/xhtml+xml',
        [REQUEST_HEADER]: 'sign_in',
      },
    });
  } catch {
    // Réseau coupé : le navigateur saura dire pourquoi, lui.
    form.submit();
    return;
  }

  const location = response.ok ? sameOriginUrl(response.headers.get(LOCATION_HEADER)) : null;
  if (location) {
    // Navigation pleine page et non visite Turbo : l'utilisateur de session change, comme
    // pour le masquerade (cf. `navigate` dans masquerade_vortex.ts).
    runVortexTransition(form.dataset.authVortex, () => window.location.assign(location));
    return;
  }

  if (await adoptDevisePage(response)) return;

  // Réponse inattendue : on rend la main au navigateur, qui soumet comme avant le fetch et
  // affiche ce que le serveur répond. Le spinner reste en place jusque-là.
  form.submit();
}

export function initializeAuthVortex() {
  if ((window as any).__authVortexInitialized) return;
  (window as any).__authVortexInitialized = true;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;
      if (event.defaultPrevented) return;

      // On reprend la main sur le départ, dans les deux sens. Déconnexion : le vortex a
      // besoin de ses ~220 ms d'implosion avant que la requête parte, faute de quoi une
      // réponse rapide couperait la scène en deux. Connexion : la requête part en fetch, et
      // la suite dépend de ce qu'elle répond.
      event.preventDefault();
      // Seconde soumission : le départ est déjà engagé. On écoute en capture, donc AVANT
      // `form_loading_controller.js` — sans cette garde, un double clic rejouerait
      // l'implosion, ou renverrait les identifiants.
      if (departed.has(form)) return;
      departed.add(form);

      if (directionOf(form) === 'sign_in') {
        void signIn(form);
        return;
      }

      // `runVortexTransition` rend la main immédiatement quand il ne joue rien (charge utile
      // illisible, mouvement réduit) — la soumission a lieu dans tous les cas.
      runVortexTransition(form.dataset.authVortex, () => {
        // `submit()` et non `requestSubmit()` : le second réémettrait l'événement, donc
        // rejouerait l'implosion. Le bouton qui a servi à soumettre n'a rien à transmettre
        // ici — ces formulaires n'ont qu'un seul bouton et aucune valeur dessus.
        form.submit();
      });
    },
    // Capture : la scène doit partir même si un autre écouteur arrête la propagation avant
    // nous (le dropdown Bootstrap du compte qui se referme, par exemple).
    true
  );

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) departed = new WeakSet();
  });
}
